<template>
  <div ref="rootRef" class="notif-root">
    <button
      type="button"
      class="notif-row"
      :class="{ rail: collapsed }"
      :aria-label="rowAriaLabel"
      :aria-expanded="open ? 'true' : 'false'"
      @click="togglePanel"
    >
      <StrokeIcon :paths="ICONS.bell" />
      <span class="notif-rowlabel">Notifications</span>
      <span class="notif-rlabel">Notifs</span>
      <span v-if="unreadCount > 0" class="notif-pill">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <div v-if="open" class="notif-panel" :class="{ rail: collapsed }">
      <div class="notif-head">
        <span class="notif-title">Notifications</span>
        <button
          v-if="unreadCount > 0"
          type="button"
          class="notif-markall"
          :disabled="markingAllRead"
          @click="handleMarkAllAsRead"
        >
          Tout marquer comme lu
        </button>
      </div>

      <template v-if="notifications.length > 0">
        <div class="notif-list">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notif-item"
            :class="{ unread: !notification.is_read }"
            @click="handleNotificationClick(notification)"
          >
            <span class="notif-ico" :class="typeTint(notification.type)">
              <StrokeIcon :paths="typeIcon(notification.type)" :size="16" :stroke-width="1.9" />
            </span>
            <span class="notif-body">
              <span class="notif-ititle">{{ notification.title }}</span>
              <div class="notif-msg">{{ notification.message }}</div>
              <div class="notif-foot">
                <span class="notif-time">{{ formatTime(notification.created_at) }}</span>
                <button
                  type="button"
                  class="notif-del"
                  aria-label="Supprimer cette notification"
                  @click.stop="handleDelete(notification.id)"
                >
                  <StrokeIcon :paths="ICONS.x" :size="12" :stroke-width="2.2" />
                  Supprimer
                </button>
              </div>
            </span>
          </div>
        </div>
        <button
          v-if="hasMore"
          type="button"
          class="notif-seeall"
          :disabled="isLoading"
          @click.stop="loadMore"
        >
          {{ isLoading ? 'Chargement…' : 'Voir plus' }}
        </button>
      </template>

      <div v-else class="notif-empty">
        <StrokeIcon :paths="ICONS.bellOff" :size="34" :stroke-width="1.6" />
        <div class="notif-empty-title">Aucune notification</div>
        <div class="notif-empty-text">
          Vous serez prévenu ici des nouvelles demandes, des rappels programmés et des messages des formateurs.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * Notifications de la barre v2 : la cloche icône-seule de la v1 devient une
 * rangée « Notifications » nommée (canvas « Barre de navigation v2 »).
 * Même moteur que la v1 (composable useNotifications) : compteur plafonné
 * 99+, panneau ancré au-dessus du pied (à droite du rail en mode réduit),
 * non-lues à liseré, clic = lue + navigation, suppression nommée, Voir plus.
 */
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import { useNotifications } from '../../composables/useNotifications'
import { useClickOutside } from '../../composables/useClickOutside.js'
import { StrokeIcon } from '../ui'

const props = defineProps({
  collapsed: { type: Boolean, default: false }
})

const router = useRouter()

const {
  unreadCount,
  notifications,
  isLoading,
  hasMore,
  fetchNotifications,
  loadMore,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  startPolling,
  stopPolling
} = useNotifications()

const ICONS = {
  bell: ['M18 9a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8', 'M13.7 21a2 2 0 0 1-3.4 0'],
  bellOff: ['M18 9a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8', 'M13.7 21a2 2 0 0 1-3.4 0', 'M3 3l18 18'],
  x: ['M18 6L6 18', 'M6 6l12 12'],
  clipboard: ['M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2', 'M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z', 'M9 12h6M9 16h4'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2'],
  msg: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
  user: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
  check: ['M20 6L9 17l-5-5'],
  swap: ['M17 3l4 4-4 4', 'M21 7H7', 'M7 21l-4-4 4-4', 'M3 17h14'],
  calcheck: ['M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z', 'M16 3v4M8 3v4M3 11h18', 'M9.5 15.5l2 2 3.5-3.5'],
  card: ['M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z', 'M2 10h20'],
  warn: ['M12 3L1.5 21h21L12 3z', 'M12 10v5', 'M12 18.2h.01'],
  filesign: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5', 'M8.5 16.5c1-1 2 1 3 0s2 1 3 0'],
  redo: ['M3 12a9 9 0 1 0 3-6.7L3 8', 'M3 3v5h5'],
  box: ['M21 8l-9-5-9 5v8l9 5 9-5V8z', 'M3 8l9 5 9-5', 'M12 13v8'],
  invoice: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5', 'M9 13h6M9 17h3'],
  phoneOff: ['M10.7 6.5A15 15 0 0 1 9 4H5a2 2 0 0 0-2 2 16 16 0 0 0 7.6 12.4', 'M15 13.5c1.5 1 3 1.5 4.5 1.5a2 2 0 0 1 2 2v3', 'M3 3l18 18'],
  bank: ['M3 21h18', 'M5 21V10h14v11', 'M12 3L3 8h18l-9-5z', 'M9 14v4M15 14v4']
}

// Pictogramme et teinte par type — mêmes familles sémantiques que la v1
// (getNotificationIcon/getNotificationColor de l'ancienne cloche).
const TYPE_STYLES = {
  quote_new: { icon: 'clipboard', tint: 't-primary' },
  quote_assigned: { icon: 'user', tint: 't-primary' },
  quote_unassigned: { icon: 'user', tint: 't-neutral' },
  quote_status_changed: { icon: 'swap', tint: 't-info' },
  comment_added: { icon: 'msg', tint: 't-info' },
  reminder: { icon: 'clock', tint: 't-warning' },
  quote_availability_submitted: { icon: 'calcheck', tint: 't-success' },
  quote_appointment_requested: { icon: 'calcheck', tint: 't-primary' },
  prestataire_dispo_oui: { icon: 'check', tint: 't-success' },
  prestataire_dispo_non: { icon: 'x', tint: 't-danger' },
  formulaire_stagiaire_rempli: { icon: 'user', tint: 't-success' },
  paiement_stripe_recu: { icon: 'card', tint: 't-success' },
  virement_recu: { icon: 'bank', tint: 't-success' },
  formulaire_non_rempli_relance: { icon: 'warn', tint: 't-warning' },
  prestataire_non_repondu_relance: { icon: 'redo', tint: 't-warning' },
  convention_prestataire_retard: { icon: 'filesign', tint: 't-warning' },
  formateur_disponible: { icon: 'check', tint: 't-success' },
  formateur_non_disponible: { icon: 'x', tint: 't-danger' },
  mission_attribuee_notif: { icon: 'clipboard', tint: 't-primary' },
  questionnaire_rempli: { icon: 'check', tint: 't-success' },
  sms_error: { icon: 'phoneOff', tint: 't-danger' },
  facture_formateur_recue: { icon: 'invoice', tint: 't-info' },
  dossier_livre: { icon: 'box', tint: 't-success' },
  emargement_incomplet: { icon: 'filesign', tint: 't-warning' },
  paiement_retard: { icon: 'warn', tint: 't-danger' },
  general: { icon: 'bell', tint: 't-neutral' }
}

function typeIcon(type) {
  const style = TYPE_STYLES[type]
  return ICONS[style ? style.icon : 'bell']
}

function typeTint(type) {
  const style = TYPE_STYLES[type]
  return style ? style.tint : 't-primary'
}

const open = ref(false)
const markingAllRead = ref(false)
const rootRef = ref(null)

const rowAriaLabel = computed(() =>
  unreadCount.value > 0
    ? `Notifications — ${unreadCount.value} non lue${unreadCount.value > 1 ? 's' : ''}`
    : 'Notifications'
)

const togglePanel = () => {
  open.value = !open.value
  if (open.value) {
    fetchNotifications()
  }
}

const handleNotificationClick = async (notification) => {
  try {
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }
  } catch (err) {
    console.error('Error marking notification as read:', err)
  }
  open.value = false

  if (notification.lien) {
    router.push(notification.lien)
  } else if (notification.quote_id) {
    router.push(`/quotes/${notification.quote_id}`)
  } else if (notification.inscription_id) {
    router.push(`/inscriptions/${notification.inscription_id}`)
  }
}

const handleMarkAllAsRead = async () => {
  markingAllRead.value = true
  try {
    await markAllAsRead()
    toast.success('Toutes les notifications ont été marquées comme lues')
  } catch (err) {
    toast.error('Erreur lors du marquage des notifications')
    console.error(err)
  } finally {
    markingAllRead.value = false
  }
}

const handleDelete = async (id) => {
  try {
    await deleteNotification(id)
  } catch (err) {
    toast.error('Erreur lors de la suppression')
    console.error(err)
  }
}

const formatTime = (dateString) => {
  if (!dateString) return 'Date inconnue'

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Date invalide'

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  if (diffMs < 0) return "À l'instant"

  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "À l'instant"
  if (diffMins < 60) return `Il y a ${diffMins} min`
  if (diffHours < 24) return `Il y a ${diffHours} h`
  if (diffDays < 7) return `Il y a ${diffDays} j`
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

useClickOutside(rootRef, () => { open.value = false })

onMounted(() => {
  fetchNotifications()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped>
.notif-root { position: relative; }

.notif-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  color: var(--color-text-secondary);
  cursor: pointer;
  text-align: left;
  transition: background 150ms, color 150ms;
}
.notif-row svg { color: var(--color-text-tertiary); }
.notif-row:hover { background: var(--color-bg-tertiary); color: var(--color-text-primary); }
.notif-row:focus-visible { outline: none; box-shadow: var(--focus-ring); }

.notif-rowlabel {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.notif-rlabel { display: none; }

.notif-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  background: var(--color-primary);
  color: #fff;
  line-height: 1.3;
  flex-shrink: 0;
}

.notif-row.rail {
  flex-direction: column;
  gap: 4px;
  padding: 8px 4px;
  min-height: 56px;
}
.notif-row.rail .notif-rowlabel { display: none; }
.notif-row.rail .notif-rlabel {
  display: block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.15;
  text-align: center;
  width: 100%;
}
.notif-row.rail .notif-pill {
  position: absolute;
  top: 3px;
  right: 6px;
  min-width: 20px;
  padding: 1px 6px;
}

.notif-panel {
  position: absolute;
  bottom: calc(100% + 8px);
  left: -2px;
  width: 370px;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.14);
  z-index: 80;
  overflow: hidden;
}
.notif-panel.rail {
  left: calc(100% + 10px);
  width: 370px;
  bottom: 0;
}

@media (max-width: 768px) {
  /* Dans le tiroir mobile, le panneau reste à l'intérieur du tiroir. */
  .notif-panel {
    width: auto;
    left: -2px;
    right: -2px;
  }
}

.notif-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--color-border-light);
}
.notif-title { font-size: 15px; font-weight: 600; color: var(--color-text-primary); }

.notif-markall {
  display: flex;
  align-items: center;
  gap: 5px;
  border: 0;
  background: transparent;
  color: var(--color-info-dark);
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  min-height: 36px;
  flex-shrink: 0;
}
.notif-markall:hover { background: var(--color-info-lighter); }
.notif-markall:disabled { opacity: 0.6; cursor: default; }

.notif-list {
  max-height: 420px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;
}
.notif-list::-webkit-scrollbar { width: 6px; }
.notif-list::-webkit-scrollbar-thumb { background: var(--color-border); border-radius: 3px; }

.notif-item {
  display: flex;
  gap: 10px;
  padding: 12px 14px 12px 11px;
  border-bottom: 1px solid var(--color-bg-tertiary);
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: background 150ms;
}
.notif-item:hover { background: var(--color-bg-secondary); }
.notif-item.unread {
  border-left-color: var(--color-primary);
  background: #fffcfb;
}

.notif-ico {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.notif-ico.t-primary { background: var(--color-primary-lightest); color: var(--color-primary); }
.notif-ico.t-danger { background: var(--color-danger-lightest); color: var(--color-danger-dark); }
.notif-ico.t-info { background: var(--color-info-lighter); color: var(--color-info-dark); }
.notif-ico.t-success { background: var(--color-success-lightest); color: var(--color-success-dark); }
.notif-ico.t-warning { background: var(--color-warning-lighter); color: var(--color-warning-dark); }
.notif-ico.t-neutral { background: var(--color-bg-tertiary); color: var(--color-text-tertiary); }

.notif-body { flex: 1; min-width: 0; }
.notif-ititle { font-size: 13.5px; font-weight: 600; line-height: 1.3; color: var(--color-text-primary); }
.notif-msg { font-size: 12.5px; color: var(--color-text-tertiary); line-height: 1.4; margin-top: 2px; }

.notif-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
}
.notif-time { font-size: 12px; color: var(--color-text-muted); }

.notif-del {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  min-height: 36px;
  padding: 4px 8px;
  margin-right: -8px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 150ms, color 150ms;
}
.notif-del:hover { background: var(--color-danger-lightest); color: var(--color-danger-dark); }

.notif-seeall {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 14px 12px;
  min-height: 40px;
  width: calc(100% - 28px);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  color: var(--color-text-primary);
  cursor: pointer;
}
.notif-seeall:hover { background: var(--color-bg-tertiary); border-color: var(--color-border-dark); }
.notif-seeall:disabled { opacity: 0.6; cursor: default; }

.notif-empty {
  padding: 34px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  color: var(--color-text-muted);
}
.notif-empty-title { font-size: 14px; font-weight: 600; color: var(--color-text-secondary); }
.notif-empty-text { font-size: 12.5px; color: var(--color-text-tertiary); line-height: 1.5; }
</style>
